import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { tenantApi } from '../../services/api';
import { ArrowLeftIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const TenantEmailTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [defaultTemplates, setDefaultTemplates] = useState(null);
  const [availableVariables, setAvailableVariables] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      useCustomTemplate: false,
      useHtmlEmail: true,
      emailSubjectTemplate: '',
      emailBodyTemplate: '',
      emailBodyTextTemplate: ''
    }
  });

  const useCustomTemplate = watch('useCustomTemplate');
  const useHtmlEmail = watch('useHtmlEmail');

  // Fetch tenant email template data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [tenantResponse, templateResponse] = await Promise.all([
          tenantApi.getById(id),
          tenantApi.getEmailTemplate(id)
        ]);

        setTenant(tenantResponse.data);
        setDefaultTemplates(templateResponse.data.defaultTemplates);
        setAvailableVariables(templateResponse.data.availableVariables);

        reset({
          useCustomTemplate: templateResponse.data.useCustomTemplate,
          useHtmlEmail: templateResponse.data.useHtmlEmail !== undefined ? templateResponse.data.useHtmlEmail : true,
          emailSubjectTemplate: templateResponse.data.emailSubjectTemplate || '',
          emailBodyTemplate: templateResponse.data.emailBodyTemplate || '',
          emailBodyTextTemplate: templateResponse.data.emailBodyTextTemplate || ''
        });
      } catch (error) {
        toast.error('Failed to load email template');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await tenantApi.updateEmailTemplate(id, data);
      toast.success('Email template updated successfully');
      navigate('/tenants');
    } catch (error) {
      toast.error('Failed to update email template');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyVariableToClipboard = (variable) => {
    navigator.clipboard.writeText(`{{.${variable}}}`);
    toast.success(`Copied {{.${variable}}} to clipboard`);
  };

  return (
    <div className="card max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(`/tenants/${id}/edit`)}
          className="text-gray-600 hover:text-gray-900 mr-4"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Email Template Settings
          </h1>
          {tenant && (
            <p className="text-gray-600">
              {tenant.firstName} {tenant.lastName}
            </p>
          )}
        </div>
      </div>

      {loading && !tenant ? (
        <div className="text-center py-4">Loading template data...</div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-6">
            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Template Variables</p>
                  <p className="mb-2">
                    You can use the following variables in your templates. Click to copy:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {availableVariables.map((variable) => (
                      <button
                        key={variable}
                        type="button"
                        onClick={() => copyVariableToClipboard(variable)}
                        className="bg-white border border-blue-300 rounded px-2 py-1 text-xs hover:bg-blue-100 transition-colors"
                      >
                        {`{{.${variable}}}`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Use Custom Template Toggle */}
            <div className="flex items-center">
              <input
                id="useCustomTemplate"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                {...register('useCustomTemplate')}
              />
              <label htmlFor="useCustomTemplate" className="ml-2 block text-sm text-gray-900">
                Use custom email template for this tenant
              </label>
            </div>

            {useCustomTemplate && (
              <>
                {/* HTML Email Toggle */}
                <div className="flex items-center bg-gray-50 p-4 rounded-md">
                  <input
                    id="useHtmlEmail"
                    type="checkbox"
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    {...register('useHtmlEmail')}
                  />
                  <label htmlFor="useHtmlEmail" className="ml-2 block text-sm text-gray-900">
                    Send HTML emails (uncheck for text-only emails)
                  </label>
                </div>

                {/* Subject Template */}
                <div>
                  <label htmlFor="emailSubjectTemplate" className="form-label">
                    Email Subject Template
                  </label>
                  <input
                    id="emailSubjectTemplate"
                    type="text"
                    className="form-input"
                    placeholder={defaultTemplates?.subject || ''}
                    {...register('emailSubjectTemplate')}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Default: {defaultTemplates?.subject}
                  </p>
                </div>

                {/* HTML Body Template - only show if HTML emails are enabled */}
                {useHtmlEmail && (
                  <div>
                    <label htmlFor="emailBodyTemplate" className="form-label">
                      Email HTML Body Template
                    </label>
                    <textarea
                      id="emailBodyTemplate"
                      rows="15"
                      className="form-input font-mono text-sm"
                      placeholder={defaultTemplates?.htmlBody || ''}
                      {...register('emailBodyTemplate')}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      HTML template with styling. Leave empty to use default.
                    </p>
                  </div>
                )}

                {/* Text Body Template */}
                <div>
                  <label htmlFor="emailBodyTextTemplate" className="form-label">
                    Email Plain Text Body Template
                  </label>
                  <textarea
                    id="emailBodyTextTemplate"
                    rows="10"
                    className="form-input font-mono text-sm"
                    placeholder={defaultTemplates?.textBody || ''}
                    {...register('emailBodyTextTemplate')}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Plain text version of the email. Leave empty to use default.
                  </p>
                </div>

                {/* Default Templates Reference */}
                <div className="border-t pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                  >
                    {showPreview ? 'Hide' : 'Show'} Default Templates
                  </button>

                  {showPreview && defaultTemplates && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">
                          Default Subject:
                        </h3>
                        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                          {defaultTemplates.subject}
                        </pre>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">
                          Default Text Body:
                        </h3>
                        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                          {defaultTemplates.textBody}
                        </pre>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">
                          Default HTML Body:
                        </h3>
                        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto max-h-96">
                          {defaultTemplates.htmlBody}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {!useCustomTemplate && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  This tenant will use the default email template. Enable custom template above to customize the email content.
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate(`/tenants/${id}/edit`)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default TenantEmailTemplate;
